import React, { useEffect, useState } from "react";
import { Nav, Image } from "react-bootstrap";
import { NavLink, useLocation, useNavigate } from "react-router-dom";
import "../../../assets/css/Sidebar.css";
import { useTranslate } from "../../../hooks/LanguageProvider";
import Logout from "../../Logout/Logout";
import usePermissions from "../../../hooks/usePermissions";
import { useUser } from "../../../context/UserContext";
import { useAuth } from "../../../context/AuthContext";
import { menuItems1 } from "../../../config/menu/menu1";
import { menuItems2 } from "../../../config/menu/menu2";
import { menuItems3 } from "../../../config/menu/menu3";
import { menuItems4 } from "../../../config/menu/menu4";
import type { MenuItem } from "../../../config/menu/menu.types";

interface SidebarProps {
  onSidebarStateChange: (isOpen: boolean) => void;
}

const Sidebar: React.FC<SidebarProps> = ({ onSidebarStateChange }) => {
  const { translate } = useTranslate();
  const [isOpen, setIsOpen] = useState("");
  const [activeMenuText, setActiveMenuText] = useState("iq-menu-span-hide");
  const [menuButtonSidebar, setMenuButtonSidebar] = useState(
    "iq-menu-bt-sidebar-hide"
  );
  const [sidebar, setSidebar] = useState("sidebar-close");
  const [openSubmenus, setOpenSubmenus] = useState<string[]>([]);
  const { pathImg } = useUser();

  const navigate = useNavigate();
  const location = useLocation();
  const { logout } = useAuth();

  const [isSmallScreen, setIsSmallScreen] = useState<boolean>(
    window.matchMedia("(max-width: 1299px)").matches
  );

  useEffect(() => {
    const mediaQuery = window.matchMedia("(max-width: 1299px)");

    const handleMediaQueryChange = (event: MediaQueryListEvent) => {
      setIsSmallScreen(event.matches);
    };

    if (typeof mediaQuery.addEventListener === "function") {
      mediaQuery.addEventListener("change", handleMediaQueryChange);
      return () => {
        mediaQuery.removeEventListener("change", handleMediaQueryChange);
      };
    }

    mediaQuery.addListener(handleMediaQueryChange);
    return () => {
      mediaQuery.removeListener(handleMediaQueryChange);
    };
  }, []);

  const roleId = localStorage.getItem("id_role");
  const numericRoleId = Number(roleId || 0);
  const { userPermissions } = usePermissions(roleId);

  const hasPermission = (permissionId: number): boolean =>
    permissionId === 0 ||
    userPermissions.some(
      (permission) =>
        permission.id_permission === permissionId && permission.can_read === 1
    );

  const checkAccess = (item: MenuItem): boolean => {
    const hasRole =
      !item.allowedRoles || item.allowedRoles.includes(numericRoleId);

    return hasPermission(item.permissionId) && hasRole;
  };

  const handleSubmenuClick = (submenuId: string): void => {
    setOpenSubmenus((prev) =>
      prev.includes(submenuId) ? [] : [submenuId]
    );
  };

  const handleSetIsOpen = () => {
    const nextIsOpen = sidebar === "sidebar-close";

    setIsOpen((prev) => (prev === "open" ? "" : "open"));
    setSidebar((prev) =>
      prev === "sidebar-close" ? "sidebar-open" : "sidebar-close"
    );
    setActiveMenuText((prev) =>
      prev === "iq-menu-span-hide" ? "" : "iq-menu-span-hide"
    );
    setMenuButtonSidebar((prev) =>
      prev === "iq-menu-bt-sidebar-show"
        ? "iq-menu-bt-sidebar-hide"
        : "iq-menu-bt-sidebar-show"
    );

    onSidebarStateChange(nextIsOpen);
  };

  const handleLogout = async () => {
    try {
      await logout();
      navigate("/login");
    } catch (error) {
      console.error("Erreur pendant la déconnexion :", error);
    }
  };

  const handleMouseEnter = () => {
    if (isSmallScreen) return;

    setIsOpen("open");
    setSidebar("sidebar-open");
    setActiveMenuText("");
    setMenuButtonSidebar("iq-menu-bt-sidebar-show");
    onSidebarStateChange(true);
  };

  const handleMouseLeave = () => {
    if (isSmallScreen) return;

    setIsOpen("");
    setSidebar("sidebar-close");
    setActiveMenuText("iq-menu-span-hide");
    setMenuButtonSidebar("iq-menu-bt-sidebar-hide");
    onSidebarStateChange(false);
  };

  const handleMenuNavigation = () => {
    setOpenSubmenus([]);

    if (isSmallScreen) {
      handleSetIsOpen();
    }
  };

  const isActiveRoute = (to?: string) => {
    if (!to) return false;
    if (to === "/") return location.pathname === "/";
    return location.pathname.toLowerCase().startsWith(to.toLowerCase());
  };

  const menuSections = [menuItems1, menuItems2, menuItems3, menuItems4];

  return (
    <div
      style={{ zIndex: 10015 }}
      className={`iq-sidebar sidebar-default ${sidebar}`}
      onMouseEnter={handleMouseEnter}
      onMouseLeave={handleMouseLeave}
    >
      <div className="iq-sidebar-logo d-flex align-items-center justify-content-between">
        {sidebar === "sidebar-open" && (
          <Nav.Link to="/" className="header-logo header-logo-show" as={NavLink}>
            <Image
              className="img-fluid rounded-normal light-logo"
              style={{
                height: "auto",
                maxHeight: "50px",
                objectFit: "contain",
              }}
              src={"asset/images/logo.png"}
            />
          </Nav.Link>
        )}

        <div
          className={`iq-menu-bt-sidebar ml-0 ${menuButtonSidebar}`}
          onClick={handleSetIsOpen}
        >
          <i className={`ri-menu-2-line wrapper-menu ${isOpen}`}></i>
        </div>
      </div>

      <div
        className="data-scrollbar"
        data-scroll="1"
        data-scrollbar="true"
        style={{ overflow: "hidden", outline: "none" }}
      >
        <div className="scroll-content">
          <nav className="iq-sidebar-menu">
            {menuSections.map((menuSection, index) => (
              <ul key={`menu-section-${index}`} className="iq-menu">
                {index > 0 && (
                  <li className="divider" style={{ margin: "0 0 5px" }}></li>
                )}

                {menuSection.map((menuItem) =>
                  checkAccess(menuItem) ? (
                    menuItem.divider ? (
                      <li key={menuItem.id}></li>
                    ) : (
                      <li key={menuItem.id}>
                        {menuItem.to ? (
                          <Nav.Link
                            as={NavLink}
                            to={menuItem.to}
                            className={`svg-icon ${isActiveRoute(menuItem.to) ? "active" : ""}`}
                            onClick={handleMenuNavigation}
                          >
                            <i className={menuItem.icon} />
                            <span className={`menu-label ${activeMenuText}`}>
                              {translate(menuItem.label)}
                            </span>
                          </Nav.Link>
                        ) : (
                          <Nav.Link
                            onClick={() => handleSubmenuClick(menuItem.label)}
                            aria-expanded={openSubmenus.includes(menuItem.label)}
                          >
                            <i className={menuItem.icon} />
                            <span className={`menu-label ${activeMenuText}`}>
                              {translate(menuItem.label)}
                            </span>

                            <svg
                              style={{ minWidth: "fit-content" }}
                              className="svg-icon iq-arrow-right arrow-active"
                              width="20"
                              height="20"
                              xmlns="http://www.w3.org/2000/svg"
                              viewBox="0 0 24 24"
                              fill="none"
                              stroke="currentColor"
                              strokeWidth="2"
                              strokeLinecap="round"
                              strokeLinejoin="round"
                            >
                              <polyline points="10 15 15 20 20 15"></polyline>
                              <path d="M4 4h7a4 4 0 0 1 4 4v12"></path>
                            </svg>
                          </Nav.Link>
                        )}

                        {menuItem.subItems && (
                          <ul
                            className={`iq-submenu ${
                              openSubmenus.includes(menuItem.label)
                                ? "submenu-enter-active"
                                : "submenu-enter"
                            }`}
                          >
                            {menuItem.subItems.map(
                              (subItem) =>
                                checkAccess(subItem) && (
                                  <li key={subItem.id}>
                                    <Nav.Link
                                      as={NavLink}
                                      to={subItem.to || "/"}
                                      className={`svg-icon ${isActiveRoute(subItem.to) ? "active" : ""}`}
                                      onClick={handleMenuNavigation}
                                    >
                                      <i className={subItem.icon} />
                                      <span className="menu-label">
                                        {translate(subItem.label)}
                                      </span>
                                    </Nav.Link>
                                  </li>
                                )
                            )}
                          </ul>
                        )}
                      </li>
                    )
                  ) : null
                )}
              </ul>
            ))}
          </nav>

          <div
            className="position-relative sidebar-bottom"
            style={{ margin: 0 }}
          >
            <nav className="iq-sidebar-menu">
              <ul className="iq-menu" style={{ padding: 0 }}>
                <li className="divider" style={{ margin: "0 0 5px" }}></li>
                <Logout
                  onLogout={handleLogout}
                  activeMenu={activeMenuText}
                  title={translate("Logout")}
                  margin="menu-label"
                />
              </ul>
            </nav>
          </div>
        </div>
      </div>
    </div>
  );
};

export default Sidebar;
